import { cva } from 'class-variance-authority';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import Path from '../../Path';
import { useLanguage } from '../../i18n/useLanguage';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { cn } from '../utils/cn';

import SearchBar from './SearchBar';
import ViewSwitch from './ViewSwitch';
import type { LayoutVariant, NavItem } from './layoutTypes';

interface HeaderProps {
    variant: LayoutVariant;
    navItems?: NavItem[];
    onSearch?: (query: string) => void;
}

// map : carte flottante au-dessus de la carte (comme la recherche de Google Maps) ; seule la
// carte reçoit les clics (pointer-events-auto), le reste du conteneur laisse passer ceux de la carte.
// standard : barre collée en haut de page (comme Airbnb).
const headerVariants = cva('', {
    variants: {
        variant: {
            map: 'pointer-events-auto mx-2 mt-2 rounded-2xl border border-gray-200 bg-white/90 shadow-lg backdrop-blur dark:border-gray-700 dark:bg-gray-900/90 sm:mx-3 sm:mt-3 lg:mx-auto lg:w-full lg:max-w-5xl',
            standard:
                'sticky top-0 z-30 border-b border-gray-200 bg-white/90 backdrop-blur dark:border-gray-800 dark:bg-gray-950/90',
        },
    },
});

// Mobile : ligne 1 = logo + actions, ligne 2 = recherche pleine largeur. À partir de md : une seule ligne.
const containerVariants = cva(
    'flex flex-wrap items-center gap-x-3 gap-y-2 px-3 sm:px-4 md:gap-x-4',
    {
        variants: {
            variant: {
                map: 'py-2',
                standard: 'mx-auto max-w-7xl py-3 lg:px-8',
            },
        },
    },
);

const searchVariants = cva('order-last basis-full md:order-none md:flex-1 md:basis-auto', {
    variants: {
        variant: {
            map: '',
            standard: 'md:max-w-xl',
        },
    },
});

/** Header réutilisé par les deux variantes du layout : même contenu, habillage différent. */
export default function Header({ variant, navItems = [], onSearch }: HeaderProps) {
    const { t } = useTranslation();
    const { localize } = useLanguage();

    return (
        <header className={headerVariants({ variant })}>
            <div className={containerVariants({ variant })}>
                <Link
                    to={localize(Path.MAP)}
                    aria-label={t('layout.homeLink')}
                    className="inline-flex items-center gap-2 rounded text-lg font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-white"
                >
                    <MapPin aria-hidden="true" className="size-5 shrink-0" />
                    <span>{t('app.name')}</span>
                </Link>

                <SearchBar onSearch={onSearch} className={searchVariants({ variant })} />

                <div className="ml-auto flex items-center gap-2 sm:gap-3 md:gap-4">
                    {navItems.length > 0 && (
                        // Masquée sous md : le bouton de bascule de vue et le logo suffisent sur mobile.
                        <nav aria-label={t('layout.nav.label')} className="max-md:hidden">
                            <ul className="flex items-center gap-1 text-sm font-medium">
                                {navItems.map((item) => (
                                    <li key={item.to}>
                                        <NavLink
                                            to={item.to}
                                            className={({ isActive }) =>
                                                cn(
                                                    'rounded px-3 py-2 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-900 dark:hover:bg-gray-800 dark:focus-visible:outline-white',
                                                    isActive && 'bg-gray-100 dark:bg-gray-800',
                                                )
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                    <ViewSwitch variant={variant} />
                    <LanguageSwitcher />
                </div>
            </div>
        </header>
    );
}
